import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useState } from 'react'
import {
  Typography,
  Card,
  CardContent,
  Avatar,
  useMediaQuery,
  useTheme,
  Box,
  Grid,
  Skeleton,
  TextField,
  Chip,
  Divider,
  IconButton,
  Paper
} from '@mui/material';
import { useOrgUser } from '../context/OrgUserProvider';
import { getCountryNameById, getDefaultCompanyProfile } from '../api/userApi.js'
import { toast } from 'react-toastify'
import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import NoteIcon from '@mui/icons-material/Note';

const Profile = () => {
  const { getAccessTokenSilently, user } = useAuth0()
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { orgUser } = useOrgUser();
  const [companyProfile, setCompanyProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadCompanyProfile = async (token) => {
      try {
        const resProfile = await getDefaultCompanyProfile(token)
        if (resProfile.length > 0) {
          const resCountry = await getCountryNameById(token, {
            countryId: resProfile[0].CountryId,
          })
          setCompanyProfile({ ...resProfile[0], Country: resCountry[0].CountryDesc })
        }
      } catch (error) {
        console.error(error)
        toast.error(`Failed to load company profile: ${error.message}`)
      }
    }

    const loadDocument = async () => {
      try {
        const token = await getAccessTokenSilently({
          audience: 'https://qallix-api',
        })
        await loadCompanyProfile(token)
      } catch (error) {
        console.error(error)
        toast.error(`${error}`)
      } finally {
        setLoading(false)
      }
    }

    loadDocument()
  }, [getAccessTokenSilently, user, orgUser.orgId])

  // Loading skeleton
  if (loading) {
    return (
      <Box sx={{ p: isMobile ? 2 : 4 }}>
        <Skeleton variant="rectangular" width="100%" height={200} sx={{
          borderBottomLeftRadius: '20px',
          borderBottomRightRadius: '20px'
        }} />
        <Card sx={{ maxWidth: 1000, margin: 'auto', mt: 10, borderRadius: 3, boxShadow: 4, p: 2 }}>
          <CardContent sx={{ textAlign: 'center', mt: 6 }}>
            <Skeleton variant="circular" width={140} height={140} sx={{ margin: 'auto', mb: 2 }} />
            <Skeleton variant="text" width="60%" height={40} sx={{ margin: 'auto' }} />
            <Skeleton variant="text" width="40%" height={30} sx={{ margin: 'auto', mt: 1 }} />
            <Skeleton variant="text" width="30%" height={25} sx={{ margin: 'auto', mt: 2 }} />
          </CardContent>
        </Card>
      </Box>
    )
  }

  return (
    <Box sx={{ background: '#f9fafb', minHeight: '100vh', pb: 4 }}>
      {/* Top gradient section */}
      <Box
        sx={{
          height: 220,
          background: 'linear-gradient(135deg, #6a11cb 0%, #4361ee 100%)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
          position: 'relative',
          borderBottomLeftRadius: '20px',
          borderBottomRightRadius: '20px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}
      >
        <Avatar
          src={orgUser?.picture ?? ''}
          alt="profile"
          sx={{
            width: isMobile ? 100 : 140,
            height: isMobile ? 100 : 140,
            border: '5px solid white',
            position: 'absolute',
            bottom: -70,
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
          }}
        />
      </Box>

      <Paper 
        elevation={0}
        sx={{ 
          maxWidth: 1000, 
          margin: 'auto', 
          mt: 10, 
          borderRadius: 4, 
          boxShadow: '0 10px 30px rgba(0,0,0,0.08)', 
          p: 4,
          background: 'white'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight="600" color="text.primary">
            {companyProfile?.CompanyName || "Company Name"}
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 4 }} />
        
        <CardContent sx={{ p: 0 }}>
          <Grid container spacing={4}>
            {/* Left column */}
            <Grid item xs={12} md={6}>
              {/* Company Name */}
              <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                  <BusinessIcon sx={{ mr: 1, color: 'primary.main', fontSize: '20px' }} />
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="500">Company Name</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    size="small"
                    value={companyProfile?.CompanyName || "N/A"}
                    InputProps={{ 
                      readOnly: true,
                      sx: { 
                        borderRadius: 2,
                        background: '#f8f9fa'
                      }
                    }}
                  />
                </Grid>
              </Grid>

              {/* UEN */}
              <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                  <BusinessIcon sx={{ mr: 1, color: 'primary.main', fontSize: '20px' }} />
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="500">UEN</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField 
                    fullWidth 
                    size="small" 
                    value={companyProfile?.UEN || "N/A"} 
                    InputProps={{ 
                      readOnly: true,
                      sx: { 
                        borderRadius: 2,
                        background: '#f8f9fa'
                      }
                    }} 
                  />
                </Grid>
              </Grid>

              {/* Address */}
              <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationOnIcon sx={{ mr: 1, color: 'primary.main', fontSize: '20px' }} />
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="500">Address</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField 
                    fullWidth 
                    size="small" 
                    value={companyProfile?.Address || "N/A"} 
                    InputProps={{ 
                      readOnly: true,
                      sx: { 
                        borderRadius: 2,
                        background: '#f8f9fa'
                      }
                    }} 
                  />
                </Grid>
              </Grid>

              {/* Country */}
              <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationOnIcon sx={{ mr: 1, color: 'primary.main', fontSize: '20px' }} />
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="500">Country</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField 
                    fullWidth 
                    size="small" 
                    value={companyProfile?.Country || "N/A"} 
                    InputProps={{ 
                      readOnly: true,
                      sx: { 
                        borderRadius: 2,
                        background: '#f8f9fa'
                      }
                    }} 
                  />
                </Grid>
              </Grid>

              {/* Postal Code */}
              <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationOnIcon sx={{ mr: 1, color: 'primary.main', fontSize: '20px' }} />
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="500">Postal Code</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField 
                    fullWidth 
                    size="small" 
                    value={companyProfile?.PostalCode || "N/A"} 
                    InputProps={{ 
                      readOnly: true,
                      sx: { 
                        borderRadius: 2,
                        background: '#f8f9fa'
                      }
                    }} 
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Right column */}
            <Grid item xs={12} md={6}>
              {/* Email */}
              <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                  <EmailIcon sx={{ mr: 1, color: 'primary.main', fontSize: '20px' }} />
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="500">Email</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField 
                    fullWidth 
                    size="small" 
                    value={companyProfile?.Email || "N/A"} 
                    InputProps={{ 
                      readOnly: true,
                      sx: { 
                        borderRadius: 2,
                        background: '#f8f9fa'
                      }
                    }} 
                  />
                </Grid>
              </Grid>

              {/* Phone */}
              <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                  <PhoneIcon sx={{ mr: 1, color: 'primary.main', fontSize: '20px' }} />
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="500">Phone</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField 
                    fullWidth 
                    size="small" 
                    value={companyProfile?.Phone || "N/A"} 
                    InputProps={{ 
                      readOnly: true,
                      sx: { 
                        borderRadius: 2,
                        background: '#f8f9fa'
                      }
                    }} 
                  />
                </Grid>
              </Grid>

              {/* Person In Charge */}
              <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ mr: 1, color: 'primary.main', fontSize: '20px' }} />
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="500">Person In Charge</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField 
                    fullWidth 
                    size="small" 
                    value={companyProfile?.PersonInCharge || "N/A"} 
                    InputProps={{ 
                      readOnly: true,
                      sx: { 
                        borderRadius: 2,
                        background: '#f8f9fa'
                      }
                    }} 
                  />
                </Grid>
              </Grid>

              {/* Remark */}
              <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'flex-start', pt: 1 }}>
                  <NoteIcon sx={{ mr: 1, color: 'primary.main', fontSize: '20px', mt: 0.5 }} />
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="500">Remark</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField 
                    fullWidth 
                    size="small" 
                    multiline
                    rows={3}
                    value={companyProfile?.Remark || "N/A"} 
                    InputProps={{ 
                      readOnly: true,
                      sx: { 
                        borderRadius: 2,
                        background: '#f8f9fa'
                      }
                    }} 
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Paper>
    </Box>
  );
};

export default Profile;